import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  EmptyState,
  Modal,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import React from "react";
import * as S from "./VisitBooking.styled";
import { ISlot, IVisitWithName } from "./VisitBooking.types";
import {
  getCandidateName,
  getDayLabel,
  getEmptyStateKey,
  getMomentLabel,
  isSlotTakenError,
  getSlotKey,
  getSlotTimeLabel,
  groupSlotsPerDay,
} from "./VisitBooking.utils";

/**
 * The refusal that has its own answer. Matched on the server's wording because
 * it shares its 400 with every other validation failure, and it is the only one
 * the broker can act on without leaving the dialog.
 */

interface IBookableSlots {
  slots: ISlot[];
  capacityPerSlot: number;
  noAvailabilityReason?: string;
}

interface IProps {
  propertyId: number;
  application: IApplication;
  /** The visit being moved. Its presence is what makes this a reschedule. */
  currentVisit?: IVisitWithName;
  /** Every active visit on this property, with the candidate's name resolved. */
  visits: IVisitWithName[];
  onClose: () => void;
  onBooked: () => void;
  onOpenAvailability: () => void;
}

/**
 * The dialog a broker uses to book or move a visit while the candidate is on
 * the phone (DEV-3224).
 *
 * The bookable moments come from the server; who already stands in each of them
 * is joined in here from the visits the candidates screen has already loaded,
 * so opening this costs one request rather than one per moment.
 */
export const VisitBookingModal = (props: IProps) => {
  const {
    propertyId,
    application,
    currentVisit,
    visits,
    onClose,
    onBooked,
    onOpenAvailability,
  } = props;

  const isReschedule = !!currentVisit;
  const candidateName = getCandidateName(application);

  const [data, setData] = React.useState<IBookableSlots | undefined>();
  const [selected, setSelected] = React.useState<ISlot | undefined>();
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  // Held apart from `error`: a request that never answered says nothing about
  // the property's configuration, and the empty state does.
  const [hasLoadFailed, setHasLoadFailed] = React.useState(false);
  const currentSlotRef = React.useRef<HTMLButtonElement>(null);

  const load = React.useCallback(async () => {
    setHasLoadFailed(false);

    try {
      const result = await api.get<{ data?: IBookableSlots } & IBookableSlots>(
        `/cronofy/properties/${propertyId}/bookable-slots`,
        { isV2: true },
      );
      const body = result.data?.data || result.data;

      setData({
        slots: body?.slots || [],
        capacityPerSlot: body?.capacityPerSlot ?? 1,
        noAvailabilityReason: body?.noAvailabilityReason,
      });
    } catch {
      setData(undefined);
      setHasLoadFailed(true);
    }
  }, [propertyId]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Open the list on the moment the candidate stands in, rather than repeating
  // that moment above the list. On a property with dozens of moments it would
  // otherwise sit well below the fold and the dialog would open on free time
  // with no sign of what is being moved.
  React.useEffect(() => {
    currentSlotRef.current?.scrollIntoView({ block: "center" });
  }, [data]);

  /**
   * The moments to draw: what the server offers, plus the one the candidate
   * already stands in. That one is missing from the offer whenever it has no
   * room left, which on a property that takes one visitor per moment is always,
   * and without it the list shows an unexplained gap exactly where they are.
   */
  const slots = React.useMemo(() => {
    const offered = data?.slots || [];

    if (!currentVisit) {
      return offered;
    }

    const key = getSlotKey(currentVisit);
    if (offered.some(slot => getSlotKey(slot) === key)) {
      return offered;
    }

    return [
      ...offered,
      { start: currentVisit.start, end: currentVisit.end },
    ].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  }, [data, currentVisit]);

  /** Who already stands in a moment, and how many places are left in it. */
  const occupancy = React.useCallback(
    (slot: ISlot) => {
      const key = getSlotKey(slot);
      const booked = visits.filter(visit => getSlotKey(visit) === key);

      return {
        booked,
        free: Math.max((data?.capacityPerSlot ?? 1) - booked.length, 0),
        isCurrent: !!currentVisit && getSlotKey(currentVisit) === key,
      };
    },
    [visits, data, currentVisit],
  );

  const handleSelect = (slot: ISlot) => {
    // The banner named a moment. Leaving it up next to a fresh choice would
    // contradict the very instruction it gives.
    setError(undefined);
    setSelected(slot);
  };

  const handleConfirm = async () => {
    if (!selected) {
      return;
    }

    setIsSaving(true);
    setError(undefined);

    try {
      await api.post(`/cronofy/properties/${propertyId}/book-visit`, {
        isV2: true,
        data: {
          applicationId: application.id,
          slot: { start: selected.start, end: selected.end },
          isReschedule,
        },
      });
    } catch (unknownError) {
      // The moment can fill up between the read and the click. The server is the
      // only one that knows, so its refusal is shown here and the list is asked
      // again rather than closing the dialog on the broker mid-call.
      setError(
        isSlotTakenError(unknownError)
          ? getLocalizedText("visit_booking.error.taken")
          : getLocalizedText("visit_booking.error.generic"),
      );
      setSelected(undefined);
      setIsSaving(false);
      load();

      return;
    }

    // Outside the try on purpose: the visit exists and the candidate already has
    // the invite, so a throw from the toast or the refetch must not be reported
    // as a booking that failed and invite a second attempt.
    setIsSaving(false);
    toast({
      heading: getLocalizedText(
        isReschedule ? "visit_booking.rescheduled" : "visit_booking.booked",
        { name: candidateName, moment: getMomentLabel(selected.start) },
      ),
      variation: "success",
    });
    onBooked();
    onClose();
  };

  const renderNames = (booked: IVisitWithName[]) =>
    booked.map((visit, index) => (
      <React.Fragment key={visit.id}>
        {index > 0 && ", "}
        {visit.applicationId === application.id ? (
          <S.OwnName>{visit.name}</S.OwnName>
        ) : (
          visit.name || getLocalizedText("visit_booking.unknown_candidate")
        )}
      </React.Fragment>
    ));

  const renderSlot = (slot: ISlot) => {
    const { booked, free, isCurrent } = occupancy(slot);
    // Both would say the same thing on a property that takes one visitor per
    // moment, which is the normal one: every row would read "1 left, nobody
    // scheduled". The count earns its place only where a moment holds several.
    const showsFreeCount = (data?.capacityPerSlot ?? 1) > 1;

    return (
      <S.Slot
        key={getSlotKey(slot)}
        ref={isCurrent ? currentSlotRef : undefined}
        type="button"
        $isSelected={!!selected && getSlotKey(selected) === getSlotKey(slot)}
        $isCurrent={isCurrent}
        onClick={() => {
          if (!isCurrent) {
            handleSelect(slot);
          }
        }}
      >
        <S.SlotTop>
          <S.SlotTime $isCurrent={isCurrent}>
            {getSlotTimeLabel(slot)}
          </S.SlotTime>
          <S.SlotMeta>
            {isCurrent && getLocalizedText("visit_booking.current")}
            {!isCurrent &&
              showsFreeCount &&
              getLocalizedText("visit_booking.free", { count: `${free}` })}
          </S.SlotMeta>
        </S.SlotTop>

        {booked.length > 0 && (
          <S.SlotNames $isCurrent={isCurrent}>
            {renderNames(booked)}
          </S.SlotNames>
        )}
      </S.Slot>
    );
  };

  const renderLoadFailed = () => (
    <S.EmptyState>
      {getLocalizedText("visit_booking.error.load")}
      <S.EmptyAction>
        <Button appearance="outline" onClick={load}>
          {getLocalizedText("visit_booking.retry")}
        </Button>
      </S.EmptyAction>
    </S.EmptyState>
  );

  const renderEmpty = () => (
    <S.EmptyState>
      {getLocalizedText(getEmptyStateKey(data?.noAvailabilityReason), {
        name: candidateName,
      })}
      <S.EmptyAction>
        <Button
          appearance="outline"
          onClick={() => {
            onClose();
            onOpenAvailability();
          }}
        >
          {getLocalizedText("visit_booking.empty.action")}
        </Button>
      </S.EmptyAction>
    </S.EmptyState>
  );

  const renderBody = () => {
    if (hasLoadFailed) {
      return renderLoadFailed();
    }

    if (!data) {
      return <EmptyState isLoading size="small" />;
    }

    if (data.slots.length === 0) {
      return renderEmpty();
    }

    return groupSlotsPerDay(slots).map(day => (
      <S.Day key={day.key}>
        <S.DayHeading>{getDayLabel(day.key)}</S.DayHeading>
        {day.slots.map(renderSlot)}
      </S.Day>
    ));
  };

  const hasSlots = !!data && data.slots.length > 0;

  return (
    <Modal
      width="small"
      heading={getLocalizedText(
        isReschedule
          ? "visit_booking.reschedule.heading"
          : "visit_booking.book.heading",
      )}
      hasDismiss={true}
      onClose={onClose}
    >
      {error && (
        <Banner variation="error" hasDismiss={false}>
          {error}
        </Banner>
      )}

      {/* In the body rather than in the modal's own header: the header slot of
          this Modal is declared but never rendered, so a name put there is
          silently dropped. */}
      <S.Subtitle>
        {getLocalizedText("visit_booking.for", { name: candidateName })}
      </S.Subtitle>

      {renderBody()}

      {hasSlots && (
        <S.StickyActions>
          <Button
            appearance="primary"
            onClick={handleConfirm}
            isDisabled={!selected || isSaving}
          >
            {getLocalizedText(
              isReschedule
                ? "visit_booking.reschedule.confirm"
                : "visit_booking.book.confirm",
            )}
          </Button>
        </S.StickyActions>
      )}
    </Modal>
  );
};
